import React, {useEffect, useRef, useState} from 'react';
import {Button, Drawer} from 'antd';
import {ArrowRight, Menu, X} from 'lucide-react';
import {useLocation} from 'react-router-dom';
import {
    buildLocalizedSitePath,
    getSiteLocaleFromPathname,
    stripSiteLocaleFromPathname
} from '../../i18n/siteLocalePath';
import {useI18n} from '../../i18n/useI18n';
import {useSiteContent} from '../../pages/site/siteContent';
import LanguageSwitcher from './LanguageSwitcher';
import SiteLink from './SiteLink';
import {useContact} from './ContactModal';
import {PocDisplayConfig, systemConfigService} from '../../services/systemConfigService';
import {userService} from '../../services/userService';
import type {SiteNavItem} from '../../pages/site/site.types';
import styles from './styles/SiteHeader.module.css';

const LOGIN_POLL_INTERVAL_MS = 10_000;

const SiteHeader: React.FC = () => {
    const location = useLocation();
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [openMenuLabel, setOpenMenuLabel] = useState<string | null>(null);
    const [pocConfig, setPocConfig] = useState<PocDisplayConfig | null>(null);
    // 是否已登录（静默轮询 /users/me 检测，失败不提示）
    const [loggedIn, setLoggedIn] = useState(false);
    const closeMenuTimerRef = useRef<number | null>(null);
    const {locale, t} = useI18n();
    const {primaryCta, siteNavItems, tertiaryCta} = useSiteContent();
    const {openContact} = useContact();
    const activeLocale = getSiteLocaleFromPathname(location.pathname) ?? locale;

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 0);
        window.addEventListener('scroll', onScroll, {passive: true});
        onScroll();
        return () => window.removeEventListener('scroll', onScroll);
    }, []);

    useEffect(() => {
        setOpenMenuLabel(null);
    }, [location.pathname, location.hash]);

    useEffect(() => {
        return () => {
            if (closeMenuTimerRef.current) {
                window.clearTimeout(closeMenuTimerRef.current);
            }
        };
    }, []);

    // 静默轮询 POC 展示配置：根据后端配置变化实时更新官网 POC 菜单的展示行为
    useEffect(() => {
        loadPocConfig();
        const timer = setInterval(loadPocConfig, LOGIN_POLL_INTERVAL_MS);
        return () => clearInterval(timer);
    }, []);

    // 静默轮询 /api/v1/users/me：检测到已登录时，将「登录系统」按钮切换为「进入系统」
    useEffect(() => {
        let cancelled = false;

        const checkLogin = async () => {
            try {
                const user = await userService.getCurrentUser();
                if (!cancelled) {
                    setLoggedIn(!!(user && user.userId));
                }
            } catch {
                // 未登录或网络异常时静默忽略，不提示，保持现状
            }
        };

        checkLogin();
        const timer = setInterval(checkLogin, LOGIN_POLL_INTERVAL_MS);
        return () => {
            cancelled = true;
            clearInterval(timer);
        };
    }, []);

    const loadPocConfig = async () => {
        try {
            const response = await systemConfigService.getPocDisplayConfig();
            if ('0' === response.data.code) {
                setPocConfig(response.data.data);
            }
        } catch {
            // 静默失败：网络异常或未加载到配置时不提示，保持当前配置
        }
    };

    const headerClass = `${styles.header}${scrolled ? ` ${styles.scrolled}` : ''}`;
    const matchesPath = (targetPath: string) => {
        const [pathname, hash] = targetPath.split('#');
        if (stripSiteLocaleFromPathname(location.pathname) !== pathname) {
            return false;
        }

        if (!hash) {
            return true;
        }

        return location.hash === `#${hash}`;
    };

    const isActive = (path?: string, children?: { path: string }[]) => {
        if (path) {
            return matchesPath(path);
        }

        return children?.some(item => matchesPath(item.path)) ?? false;
    };

    const clearCloseMenuTimer = () => {
        if (closeMenuTimerRef.current) {
            window.clearTimeout(closeMenuTimerRef.current);
            closeMenuTimerRef.current = null;
        }
    };

    const scheduleCloseMenu = (label: string) => {
        clearCloseMenuTimer();
        closeMenuTimerRef.current = window.setTimeout(() => {
            setOpenMenuLabel(current => current === label ? null : label);
            closeMenuTimerRef.current = null;
        }, 120);
    };

    /**
     * 根据POC配置过滤导航菜单项
     * POC菜单过滤逻辑：
     * - 辅助交易POC: /site/poc/assist-trading
     * - 自动交易POC: /site/poc/auto-trading
     * - 形态识别POC: /site/poc/pattern
     * 当所有POC都禁用时，隐藏整个POC菜单
     */
    const filterNavItems = (items: SiteNavItem[]): SiteNavItem[] => {
        if (!pocConfig) {
            // 配置未加载时，隐藏POC菜单
            return items.filter(item => {
                if (item.children?.some(child => child.path?.startsWith('/site/poc'))) {
                    return false;
                }
                return true;
            });
        }

        return items.map(item => {
            // 处理包含子菜单的项（如POC菜单）
            if (item.children?.length) {
                // 检查是否是POC相关菜单
                const isPocMenu = item.children.some(child =>
                    child.path?.startsWith('/site/poc')
                );

                if (isPocMenu) {
                    // 过滤POC子菜单项
                    const filteredChildren = item.children.filter(child => {
                        if (!child.path?.startsWith('/site/poc')) {
                            return true;
                        }
                        if (child.path === '/site/poc/assist-trading' && pocConfig.assistTradingEnabled) {
                            return true;
                        }
                        if (child.path === '/site/poc/auto-trading' && pocConfig.autoTradingEnabled) {
                            return true;
                        }
                        if (child.path === '/site/poc/pattern' && pocConfig.patternEnabled) {
                            return true;
                        }
                        return false;
                    });

                    // 只有当有至少一个启用的POC时才显示此菜单
                    if (filteredChildren.length === 0) {
                        return null;
                    }

                    return {...item, children: filteredChildren};
                }
            }
            return item;
        }).filter(Boolean) as SiteNavItem[];
    };

    const filteredNavItems = filterNavItems(siteNavItems);

    return (
        <>
            <header className={headerClass}>
                <div className={styles.topLang}>
                    <LanguageSwitcher className={styles.languageSwitcher}/>
                </div>
                <div className={`site-content-width ${styles.inner}`}>
                    <SiteLink className={styles.brand} to="/">
                        <img alt="D-Sense" className={styles.brandLogo} src="/d-sense.png"/>
                        <strong>D-Sense</strong>
                    </SiteLink>
                    <nav className={styles.nav} data-locale={locale}>
                        {filteredNavItems.map(item => (
                            item.children?.length ? (
                                <div
                                    className={`${styles.navItem} ${openMenuLabel === item.label ? styles.navItemOpen : ''}`}
                                    key={item.label}
                                    onMouseEnter={() => {
                                        clearCloseMenuTimer();
                                        setOpenMenuLabel(item.label);
                                    }}
                                    onMouseLeave={() => {
                                        scheduleCloseMenu(item.label);
                                    }}
                                >
                                    <button
                                        className={`${styles.navButton} ${isActive(item.path, item.children) ? styles.active : ''}`}
                                        data-menu-label={item.label}
                                        onClick={() => {
                                            clearCloseMenuTimer();
                                            setOpenMenuLabel(current => current === item.label ? null : item.label);
                                        }}
                                        type="button"
                                    >
                                        {item.label}
                                    </button>
                                    <div
                                        className={styles.subMenu}
                                        data-menu-label={item.label}
                                        onMouseEnter={() => {
                                            clearCloseMenuTimer();
                                            setOpenMenuLabel(item.label);
                                        }}
                                        onMouseLeave={() => {
                                            scheduleCloseMenu(item.label);
                                        }}
                                    >
                                        {item.children.map(child => (
                                            <SiteLink
                                                className={`${styles.subMenuLink} ${matchesPath(child.path) ? styles.active : ''}`}
                                                key={child.path}
                                                onClick={() => {
                                                    clearCloseMenuTimer();
                                                    setOpenMenuLabel(null);
                                                }}
                                                to={child.path}
                                            >
                                                {child.label}
                                            </SiteLink>
                                        ))}
                                    </div>
                                </div>
                            ) : item.path === '/site/contact' ? (
                                <button
                                    className={styles.navButton}
                                    key={item.path}
                                    type="button"
                                    onClick={openContact}
                                >
                                    {item.label}
                                </button>
                            ) : (
                                <SiteLink
                                    className={`${styles.navLink} ${isActive(item.path, item.children) ? styles.active : ''}`}
                                    key={item.path}
                                    to={item.path ?? '/'}
                                >
                                    {item.label}
                                </SiteLink>
                            )
                        ))}
                    </nav>
                    <div className={styles.actions}>
                        <SiteLink className={styles.primaryButton} to={loggedIn ? '/dashboard' : tertiaryCta.path}>
                            <span>{loggedIn ? t('auth.login.enterSystem') : tertiaryCta.label}</span>
                            <ArrowRight size={16}/>
                        </SiteLink>
                    </div>
                    <button className={styles.mobileMenu} onClick={() => setOpen(true)} type="button">
                        <Menu size={20}/>
                    </button>
                </div>
            </header>
            <Drawer
                closeIcon={<X size={18}/>}
                onClose={() => setOpen(false)}
                open={open}
                placement="right"
                title={t('site.header.drawerTitle')}
            >
                <div className={styles.drawerNav}>
                    <LanguageSwitcher className={styles.drawerLanguageSwitcher}/>
                    {filteredNavItems.map(item => (
                        item.children?.length ? (
                            <div className={styles.drawerGroup} key={item.label}>
                                <span className={styles.drawerGroupTitle}>{item.label}</span>
                                {item.children.map(child => (
                                    <SiteLink
                                        className={`${styles.drawerLink} ${matchesPath(child.path) ? styles.active : ''}`}
                                        key={child.path}
                                        onClick={() => setOpen(false)}
                                        to={child.path}
                                    >
                                        {child.label}
                                    </SiteLink>
                                ))}
                            </div>
                        ) : item.path === '/site/contact' ? (
                            <button
                                className={styles.drawerLink}
                                key={item.path}
                                type="button"
                                onClick={() => {
                                    setOpen(false);
                                    setTimeout(openContact, 100);
                                }}
                            >
                                {item.label}
                            </button>
                        ) : (
                            <SiteLink
                                className={`${styles.drawerLink} ${isActive(item.path, item.children) ? styles.active : ''}`}
                                key={item.path}
                                onClick={() => setOpen(false)}
                                to={item.path ?? '/'}
                            >
                                {item.label}
                            </SiteLink>
                        )
                    ))}
                    <Button block href={loggedIn ? '/dashboard' : buildLocalizedSitePath(tertiaryCta.path, activeLocale)} size="large">
                        {loggedIn ? t('auth.login.enterSystem') : tertiaryCta.label}
                    </Button>
                    <Button block href={buildLocalizedSitePath(primaryCta.path, activeLocale)} size="large"
                            type="primary">
                        {primaryCta.label}
                    </Button>
                </div>
            </Drawer>
        </>
    );
};

export default SiteHeader;
